
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Dialog, Toast } from 'vant';
import { formatNoticeHtml } from '@/utils/migrationNotice';
import { selfSwitchFromMigratedSchool } from '@/apis/myInfo/myInfo';
import { markMigrationIgnored } from '@/utils/migrationIgnore';

const LAYER_Z = 100100;

function formatAccountDisplay(user: any): string {
  if (!user) {
    return '';
  }
  const name = String(user.username || '').trim();
  const account = String(user.account || user.mobile || user.email || '').trim();
  const role = String(user.usertype) === '1' ? '老师' : (String(user.usertype) === '0' && (name || account) ? '学生' : '');
  const parts: string[] = [];
  if (name) {
    parts.push(role ? `${name}（${role}）` : name);
  } else if (role) {
    parts.push(role);
  }
  if (account && account !== name) {
    parts.push(account);
  }
  return parts.join(' ');
}

@Component({})
export default class MigrationBlock extends Vue {
  @Prop({ default: () => ({}) }) info!: any;

  redirectSeconds = 0;
  private redirectTimer = 0;
  customSchool = '';
  switching = false;

  get phase(): number {
    return Number(this.info.phase || 2);
  }
  get cutoverStart(): number {
    return Number(this.info.cutover_start || 0);
  }
  get cutoverEnd(): number {
    return Number(this.info.cutover_end || 0);
  }
  get noticeText(): string {
    return String(this.info.notice_text || '');
  }
  get noticeHtml(): string {
    return formatNoticeHtml(this.noticeText);
  }
  get schoolName(): string {
    const fromInfo = String(this.info.school_label || '');
    if (fromInfo) return fromInfo;
    const schoolConfig = (window as any).schoolConfig || {};
    return String(schoolConfig.schoolName || '');
  }
  get newPlatformUrl(): string {
    // 移动端优先用 mobile 地址，未配置则回退 PC 地址
    return String(this.info.new_platform_url_mobile || this.info.new_platform_url || '');
  }
  get autoJump(): boolean {
    return Number(this.info.done_jump_mode || 0) === 1;
  }

  get showSelfSwitch(): boolean {
    return Number(this.info.self_switch_enabled || 0) === 1;
  }
  get targetSchool(): string {
    return String(this.info.self_switch_target_school || '');
  }
  get addVip(): number {
    return Number(this.info.self_switch_add_vip || 0);
  }
  get switchDesc(): string {
    const from = this.schoolName || '本校';
    let text = `可改学校名称并脱离${from}，以免继续被拦截。此操作不做机构认证。`;
    if (this.addVip === 1 && this.targetSchool) {
      text += ` 原校花名册认证老师切换到${this.targetSchool}后，将自动保留机构 VIP。`;
    }
    return text;
  }
  get customPlaceholder(): string {
    const from = this.schoolName || '已迁移学校';
    return `填写学校名称（不能是${from}）`;
  }

  get accountDisplay(): string {
    const user = (this.$store && this.$store.state && this.$store.state.userInfo) || {};
    return formatAccountDisplay(user);
  }

  mounted(): void {
    if (this.schoolName) {
      document.title = this.schoolName;
    }
    if (!this.accountDisplay) {
      this.$store.dispatch('setUserInfo').catch(() => { /* ignore */ });
    }
    if (this.showSelfSwitch) {
      return;
    }
    if (this.phase >= 3 && this.autoJump && this.newPlatformUrl) {
      this.redirectSeconds = 5;
      this.redirectTimer = window.setInterval(() => {
        this.redirectSeconds -= 1;
        if (this.redirectSeconds <= 0) {
          clearInterval(this.redirectTimer);
          this.gotoNewPlatform();
        }
      }, 1000);
    }
  }

  beforeDestroy(): void {
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
    }
  }

  formatTime(ts: number): string {
    if (!ts) return '--';
    const d = new Date(ts * 1000);
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  async copyUrl(): Promise<void> {
    if (!this.newPlatformUrl) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(this.newPlatformUrl);
      } else {
        const input = document.createElement('input');
        input.value = this.newPlatformUrl;
        input.setAttribute('readonly', 'readonly');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      Toast.success('链接已复制，请妥善保存');
    } catch (e) {
      Toast.fail('复制失败，请手动选中地址复制');
    }
  }

  gotoNewPlatform(): void {
    if (this.newPlatformUrl) {
      window.location.href = this.newPlatformUrl;
    }
  }

  switchToTarget(): void {
    if (!this.targetSchool) {
      return;
    }
    this.confirmSwitch(this.targetSchool, '确认切换');
  }

  switchToCustom(): void {
    const school = (this.customSchool || '').trim();
    if (!school) {
      Toast('请填写学校名称');
      return;
    }
    this.confirmSwitch(school, '确认更换');
  }

  private confirmSwitch(school: string, title: string): void {
    if (this.switching) {
      return;
    }
    const from = this.schoolName || '本校';
    Dialog.confirm({
      title,
      message: `将学校改为「${school}」并脱离${from}，确认继续？`,
      confirmButtonText: '确认切换',
      cancelButtonText: '取消',
      zIndex: LAYER_Z,
      getContainer: () => document.body,
    } as any).then(() => {
      window.setTimeout(() => {
        this.submitSwitch(school);
      }, 50);
    }).catch(() => { /* cancel */ });
  }

  private submitSwitch(school: string): void {
    if (this.switching) {
      return;
    }
    this.switching = true;
    selfSwitchFromMigratedSchool({ school }).then(() => {
      markMigrationIgnored();
      this.clearMigrationCache();
      Toast.success('已更换学校，正在恢复使用');
      const home = `${window.location.origin}${window.location.pathname}${window.location.search}#/`;
      window.setTimeout(() => {
        window.location.replace(home);
        window.location.reload();
      }, 80);
    }).catch((err: any) => {
      this.switching = false;
      if (err && err.message) {
        Toast.fail(err.message);
      }
    });
  }

  onLogout(): void {
    this.clearMigrationCache();
    localStorage.removeItem('token');
    localStorage.removeItem('token_for_login');
    localStorage.removeItem('code_key');
    sessionStorage.clear();
    this.$store.commit('setUserInfo', { id: '', username: '', usertype: '0' });
    Toast.success('已退出登录');
    this.$router.push('/login');
  }

  private clearMigrationCache(): void {
    try {
      window.localStorage.removeItem('migrationInfo');
      window.sessionStorage.removeItem('migrationInfo');
      const raw = window.localStorage.getItem('schoolConfig');
      if (raw) {
        const cfg = JSON.parse(raw);
        if (cfg && cfg.migration) {
          delete cfg.migration;
          window.localStorage.setItem('schoolConfig', JSON.stringify(cfg));
        }
      }
    } catch (e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('migrationCleared'));
  }
}
